import { tokenDeclarations } from '../shared/tokens.js';
import { logoSizeDeclarations } from './logo.js';

/**
 * 共有フォルダのページのスタイル(V02 —— #267)。
 *
 * **盤面の見た目はシェルのスタイル(`viewerStyles`)が持つ。** ここは外枠
 * (見出し・タブの帯・状態の表示)だけ。**同じトークンを使う**ので、盤面の単体ページと
 * 見た目がそろう(親 #262 の決定 5)。
 *
 * **画面の高さを埋める**(`min-height: 100dvh`。古い環境は前の `100vh` が残る)。
 * 見出しは上、盤面は残りの真ん中。盤面の高さの上限は、シェルが見出しの分を差し引く
 * (`setViewportOffset`)。
 */
export function folderStyles(): string {
  return `
.signa-folder {
${tokenDeclarations()}
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  color: var(--sp-text-primary);
  font-family: system-ui, -apple-system, 'Segoe UI', 'Hiragino Sans', 'Noto Sans JP', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  box-sizing: border-box;
}

.signa-folder *, .signa-folder *::before, .signa-folder *::after {
  box-sizing: inherit;
}

.signa-folder-header {
  flex: none;
  padding: 12px 16px 0;
  border-bottom: 1px solid var(--sp-border-default);
}

.signa-folder-header[hidden] { display: none; }

/* **1 行で省略する。** 全文は title(マウスを載せると出る)。 */
.signa-folder-title {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/*
 * **フォルダ名とロゴの行**(#318 / ADR-0053)。**ロゴを出すフォルダだけ**この行ができる。
 * フォルダ名は左、**ロゴは右端**(右上)。
 * 名前は今どおり 1 行で省略する(flex の子は min-width: 0 が無いと縮まない)。
 */
.signa-folder-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 4px;
  min-width: 0;
}

.signa-folder-heading .signa-folder-title {
  margin: 0;
  min-width: 0;
}

/* **寸法を固定する**(画像が読めなくても見出しの高さが崩れない)。 */
.signa-folder-logo { ${logoSizeDeclarations()} }

/*
 * **タブの帯は横にスクロールする**(スマートフォンの幅でタブが多いとき)。
 * 選んだタブは呼び出し側が見える位置まで送る(scrollIntoView)。
 */
.signa-folder-tabs {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scrollbar-width: thin;
}

.signa-folder-tabs[hidden] { display: none; }

.signa-folder-tab {
  appearance: none;
  flex: none;
  max-width: 16em;
  min-height: 44px;
  padding: 8px 14px;
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: var(--sp-text-secondary);
  font: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}

.signa-folder-tab:hover { color: var(--sp-text-primary); }

/* **--signa-accent には既定値を添える**(シェルの .signa-button-primary と同じ理由)。 */
.signa-folder-tab[aria-selected='true'] {
  color: var(--sp-text-primary);
  border-bottom-color: var(--signa-accent, var(--sp-accent-primary));
}

/* 帯は横にスクロールするので、外側に出す輪はかすれる。内側に描く。 */
.signa-folder-tab:focus-visible {
  outline: 2px solid var(--signa-accent, var(--sp-accent-primary));
  outline-offset: -2px;
}

/* **書き出しの間は押せない**(書き出しの途中で盤面を捨てさせない)。 */
.signa-folder-tab:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.signa-folder-panel {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
}

.signa-folder-panel[hidden] { display: none; }

/* 読み込み中・エラー・0 件。**盤面の代わりに真ん中に出す。** */
.signa-folder-status {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
  color: var(--sp-text-secondary);
}

.signa-folder-status[hidden] { display: none; }

.signa-folder-status-emphasis { color: var(--sp-text-primary); }

/* 再試行。**シェルの .signa-button と同じ見た目**(シェルが無いときにも出るので、ここに持つ)。 */
.signa-folder-retry {
  appearance: none;
  padding: 8px 16px;
  border: 1px solid var(--sp-border-strong);
  border-radius: var(--signa-radius);
  background: var(--sp-surface-raised);
  color: var(--sp-text-primary);
  font: inherit;
  cursor: pointer;
}

.signa-folder-retry:hover { background: var(--sp-surface-overlay-weak); }

.signa-folder-retry:focus-visible {
  outline: 2px solid var(--signa-accent, var(--sp-accent-primary));
  outline-offset: 2px;
}
`;
}
